/**
 * The market page's panels, each owning **its own** store subscription.
 *
 * This split is not organisational tidiness, it is the third exit criterion.
 * `useStore` is `useSyncExternalStore`, so a component that subscribes to the
 * tape re-renders whenever the tape changes — 25 times a second through the
 * final five seconds. With every subscription hoisted into `<TradeRoute>` the
 * *whole page tree* reconciles at the union of every channel's rate, which
 * mid-burst is around 40 Hz, for the benefit of one panel each time.
 *
 * Split like this, a book delta re-renders nothing (the ladder is unreconciled
 * and paints through a ref), a trade re-renders twenty rows, and a candle
 * re-renders a `<div>` and a `<canvas>` wrapper. `<TradeRoute>` itself
 * subscribes only to `market.{id}`, which changes about eleven times in a
 * market's life.
 */

import { lazy, memo, Suspense, useCallback, useMemo } from 'react'
import { Panel } from '@/components/layout'
import type { ReactNode } from 'react'
import {
  MarketChart,
  MarketStatusPill,
  OrderbookLadder,
  ResolutionPanel,
  SeriesRail,
  SettlementReceiptStack,
  StrikeMeter,
  Tape,
} from '@/components/market'
import { RAIL_SLOTS_AFTER, RAIL_SLOTS_BEFORE } from '@/components/market'
import type {
  MarketBand,
  MarketStatusPillProps,
  ProLadderPanelProps,
  RailChip,
  Timeframe,
} from '@/components/market'
import { NumCell, SegmentedControl } from '@/components/primitives'
import { isMarketId, parseMarketId, type Asset } from '@/lib/market-id'
import { useIndex, useMarket, useRuntime, useSeries, useTape, useTicker } from '@/app/use-runtime'
import { useStoreField } from '@/app/use-store'
import { cancelOwnOnRow } from '@/components/market/cancel-own'
import { ticketLinkStore } from '@/stores/ticket-link'
import { uiStore } from '@/stores/ui'
import type { Outcome } from '@/lib/decimal'
import type { LadderSide } from '@/domain/pro-ladder'
import type { MarketState } from '@/domain/lifecycle'
import type { TradingLimits } from '@/domain/order-config'
import { useIndexCandles } from '@/app/use-index-candles'
import { useServerNow } from '@/app/use-server-now'
import { formatCents, formatUsdcCompact } from '@/lib/format'
import { cx } from '@/lib/cx'

// ---------------------------------------------------------------------------

export interface StripStatsProps {
  marketId: string | null
}

/** BBO, volume and open interest, from `ticker.{id}` at 10 Hz. */
export const StripStats = memo(function StripStats({ marketId }: StripStatsProps) {
  const ticker = useTicker(marketId)
  return (
    <>
      <Stat label="UP" value={centsOf(ticker, 'up_price')} />
      <Stat label="DOWN" value={centsOf(ticker, 'down_price')} />
      <Stat label="VOL" value={usdcOf(ticker, 'volume_notional')} />
      <Stat label="OI" value={usdcOf(ticker, 'open_interest')} />
    </>
  )
})

export interface AssetSelectorProps {
  value: Asset
  /** What the venue runs: one tab per asset, and BTC alone today (§F138). */
  assets: readonly Asset[]
  onChange: (asset: Asset) => void
}

/**
 * The asset control: which market this page is.
 *
 * **It stays with one asset** (§F140). With BTC alone it has nothing to switch,
 * and it hid itself at §F138 for that reason, which left a page that named its
 * market only in a slug. A tab reading BTC is the plainest answer to "what am I
 * trading". The list is the venue's: ETH comes back if `/config` lists it.
 *
 * **It carried each asset's live index price in the `sub` slot and no longer
 * does.** Two four-digit numbers stacked under two three-letter labels inside
 * one `control-height` left neither line room to breathe, and the price is
 * already on the chart's own axis and in `<MarketStrip>` — this control's job
 * is to say which series you are looking at.
 *
 * **What went with it is a subscription that was costing more than it showed.**
 * The prices came from `useIndex` on both symbols at 5 Hz each; called from
 * `<TradeRoute>` that re-rendered the whole market page ten times a second so
 * the two numbers could move (`docs/17`), which is why this component was split
 * out at all — invariant 2, a store is subscribed by the panel that renders it.
 * With nothing here reading a store the split is no longer load-bearing, and it
 * is kept anyway: the alternative is putting an index subscription back on the
 * screen the next time this control wants live data.
 */
export const AssetSelector = memo(function AssetSelector({ value, assets, onChange }: AssetSelectorProps) {
  return (
    <SegmentedControl
      label="asset"
      size="base"
      segments={assets.map((id) => ({ id, label: id.toUpperCase() }))}
      value={value}
      onChange={onChange}
    />
  )
})

export type LiveMarketStatusPillProps = Omit<MarketStatusPillProps, 'nowMs'>

/**
 * `<MarketStatusPill>` with its own clock.
 *
 * The pill is pure and stays pure — `<SeriesRail>` renders eleven of them off
 * one instant and the gallery renders it with a fixed `nowMs` — so the
 * subscription lives in this wrapper rather than in the component. Same reason
 * as `<AssetSelector>`: `useServerNow()`'s own header says *call it as far down
 * the tree as possible*, and `<TradeRoute>` is as far up as it gets.
 */
export const LiveMarketStatusPill = memo(function LiveMarketStatusPill(
  props: LiveMarketStatusPillProps,
) {
  return <MarketStatusPill {...props} nowMs={useServerNow()} />
})

// ---------------------------------------------------------------------------

export interface ChartPanelProps {
  seriesId: string
  symbol: string
  priceDecimals?: number
  timeframe: Timeframe
  onTimeframeChange: (timeframe: Timeframe) => void
  currentMarketId: string | null
  /** Painted behind the candles. The market's countdown, from `<TradeRoute>` —
   *  this panel has no clock and should not grow one to pass it along. */
  underlay?: ReactNode
  /** The live market's span. The `market` timeframe pins the pane to it. */
  windowStartMs?: number
  windowEndMs?: number
  stale: boolean
}

/**
 * The candlestick chart of the underlying.
 *
 * It subscribes `index.{symbol}` for the live bar and `series.{id}` for the
 * markets whose strikes and windows are drawn over the price — so this panel
 * re-renders at the index's 5 Hz and nothing above it does.
 */
export const ChartPanel = memo(function ChartPanel({
  seriesId,
  symbol,
  priceDecimals,
  timeframe,
  onTimeframeChange,
  currentMarketId,
  underlay,
  windowStartMs,
  windowEndMs,
  stale,
}: ChartPanelProps) {
  const index = useIndex(symbol)
  const { candles, loading } = useIndexCandles(symbol, timeframe)
  const series = useSeries(seriesId)
  // **The live strike comes from `market.{id}`, not the rail.** Since §F73 a
  // market opens with its strike pending and the strike lands a second or two
  // later as a `state_seq` bump on the market channel. The rail announces
  // *transitions*, and a strike landing is not one — so a chart that read the
  // live strike off the rail drew no line until the market expired, or until a
  // reload re-fetched the rail's snapshot.
  const liveStrike = useMarket(currentMarketId)?.strike ?? null

  // The rail's own window is exactly the set of markets worth drawing: the
  // recently settled ones give the chart its past strikes, the live one gives
  // the line that decides the payoff.
  const bands = useMemo(
    () => withLiveStrike(bandsFrom(series, currentMarketId), currentMarketId, liveStrike),
    [series, currentMarketId, liveStrike],
  )

  return (
    <MarketChart
      symbol={symbol}
      {...(priceDecimals === undefined ? {} : { priceDecimals })}
      timeframe={timeframe}
      onTimeframeChange={onTimeframeChange}
      history={candles}
      loading={loading}
      index={index}
      bands={bands}
      currentMarketId={currentMarketId}
      {...(underlay === undefined ? {} : { underlay })}
      {...(windowStartMs === undefined ? {} : { windowStartMs })}
      {...(windowEndMs === undefined ? {} : { windowEndMs })}
      stale={stale}
    />
  )
})

/**
 * `series.{id}`'s rail entries as chart bands, **bounded to the rail's own
 * window**.
 *
 * A rail entry carries `anchor_ms`, `expiry_at_ms` and `strike`. The anchor is
 * what bounds the set below; the footprint the chart draws is the expiry and the
 * strike. The settlement window is
 * no longer among them: the chart drew it as a shaded block until it was found
 * sitting over the countdown underlay, and `chart-bands.ts` says why it went.
 *
 * **The bound is the point, and its absence was a live defect.** A past strike
 * renders as a `createPriceLine`, which spans the *whole* pane horizontally
 * whatever the time axis shows — so every market the store remembers is a
 * dashed line across the chart forever. That was invisible while the rail store
 * held one snapshot's worth of markets, and stopped being invisible when the
 * store began keeping its history: the count climbed by one a minute to the
 * store's cap, and the pane filled with lines for markets nowhere near it.
 *
 * `<ChartPanel>`'s own comment has claimed since Phase 4 that "the rail's own
 * window is exactly the set of markets worth drawing". This is what makes that
 * sentence true rather than aspirational — the same two-behind, three-ahead
 * window `<SeriesRail>` renders, off the same two constants, so the chips and
 * the strike lines cannot drift apart.
 */
/**
 * The live market's band with its strike taken from `market.{id}`.
 *
 * The market channel is the state machine's source of truth and carries the
 * strike the moment it lands; the rail entry catches up only at the market's
 * next transition. A null from the market channel never erases a strike the
 * rail already has — before the market store's first frame it is simply empty.
 */
export function withLiveStrike(bands: MarketBand[], currentMarketId: string | null, liveStrike: unknown): MarketBand[] {
  if (currentMarketId === null || liveStrike === null) return bands
  return bands.map((band) => (band.marketId === currentMarketId ? { ...band, strike: liveStrike } : band))
}

function bandsFrom(series: Record<string, unknown> | null, currentMarketId: string | null): MarketBand[] {
  const rows = series?.markets
  if (!Array.isArray(rows)) return []
  const periodMs =
    (typeof series?.period_seconds === 'number' ? series.period_seconds : 60) * 1000
  // Absent a live market there is nothing to centre on, so nothing is excluded.
  const centreMs =
    currentMarketId && isMarketId(currentMarketId) ? parseMarketId(currentMarketId).anchorMs : null
  const oldestMs = centreMs === null ? -Infinity : centreMs - RAIL_SLOTS_BEFORE * periodMs
  const newestMs = centreMs === null ? Infinity : centreMs + RAIL_SLOTS_AFTER * periodMs

  const out: MarketBand[] = []
  for (const row of rows as Record<string, unknown>[]) {
    const marketId = row.market_id
    const anchorAtMs = row.anchor_ms
    const expiryAtMs = row.expiry_at_ms
    if (typeof marketId !== 'string' || typeof anchorAtMs !== 'number' || typeof expiryAtMs !== 'number') continue
    if (anchorAtMs < oldestMs || anchorAtMs > newestMs) continue
    out.push({
      marketId,
      expiryAtMs,
      // A `SCHEDULED` market's strike is genuinely NULL, and a band with no
      // strike draws its boundary and no price line — which is the honest
      // render.
      strike: row.strike ?? null,
    })
  }
  return out
}

// ---------------------------------------------------------------------------

export interface StrikeMeterPanelProps {
  symbol: string
  strike: unknown
  stale: boolean
  /** The anchor, when the market is `OPEN` and its strike is still on its way
   *  (register §F73) — `null` otherwise. */
  pendingAtMs?: number | null
  className?: string
}

export const StrikeMeterPanel = memo(function StrikeMeterPanel({
  symbol,
  strike,
  stale,
  pendingAtMs = null,
  className,
}: StrikeMeterPanelProps) {
  const index = useIndex(symbol)
  const nowMs = useServerNow()
  return (
    <StrikeMeter
      strike={strike}
      index={index}
      nowMs={nowMs}
      stale={stale}
      pendingAtMs={pendingAtMs}
      {...(className ? { className } : {})}
    />
  )
})

// ---------------------------------------------------------------------------

export interface TapePanelProps {
  marketId: string | null
  className?: string
}

export const TapePanel = memo(function TapePanel({ marketId, className }: TapePanelProps) {
  const trades = useTape(marketId)
  return <Tape trades={trades} {...(className ? { className } : {})} />
})

// ---------------------------------------------------------------------------

export interface RailPanelProps {
  seriesId: string
  marketId: string | null
  /** `period_seconds` off `GET /config`, as a floor. The series object's own
   *  value wins where it has arrived — the anchor grid is per series. */
  periodS: number
  onSelect?: (marketId: string) => void
  /** Four chips rather than six, below 768px. Decided by the route, which
   *  already knows the breakpoint, so this panel holds no media query. */
  compact?: boolean
  className?: string
}

export const RailPanel = memo(function RailPanel({
  seriesId,
  marketId,
  periodS,
  onSelect,
  compact = false,
  className,
}: RailPanelProps) {
  const series = useSeries(seriesId)
  // The live chip's price comes from `market.{id}`, not from the rail's own
  // channel: `series.{id}` sends one snapshot and then lifecycle events, and an
  // event carries no price (§5.4, and register §F18i for why that distinction
  // had to be made in the store at all).
  const market = useMarket(marketId)
  const nowMs = useServerNow()
  const chips = (series?.markets as RailChip[] | undefined) ?? EMPTY_CHIPS
  const livePrice = market?.last_price_cents
  // Read, never assumed. `<SeriesRail>` names every anchor in its window
  // arithmetically, so a wrong period would name markets that do not exist.
  const period = typeof series?.period_seconds === 'number' ? series.period_seconds : periodS
  return (
    <SeriesRail
      chips={chips}
      currentMarketId={marketId}
      periodS={period}
      livePriceCents={typeof livePrice === 'number' ? livePrice : null}
      nowMs={nowMs}
      compact={compact}
      {...(onSelect ? { onSelect } : {})}
      {...(className ? { className } : {})}
    />
  )
})

const EMPTY_CHIPS: RailChip[] = []

// ---------------------------------------------------------------------------

export interface BookPanelProps {
  marketId: string | null
  /** `open_at_ms`, or `null` once the market is tradable. The countdown to it is
   *  derived here rather than passed, so the prop changes twice a market
   *  instead of four times a second. */
  opensAtMs: number | null
  /**
   * The order ticket sits beside the book (`lg` and up). Only then does the
   * book follow the ticket's UP/DOWN and trade on a click when the ticket's
   * "Click to trade" box is ticked — the size a click places is the ticket's
   * Shares box, and below `lg` that box is in a sheet nobody can see.
   */
  clickable?: boolean
  /** What a click needs to place an order. Read only while click to trade is on. */
  symbol?: string
  marketState?: MarketState
  openAtMs?: number
  expiryAtMs?: number
  upTokenId?: string | null
  downTokenId?: string | null
  limits?: TradingLimits
  socketStale?: boolean
  className?: string
}

/**
 * The ladder.
 *
 * Memoised on `marketId` and an instant, and it does not read a store here: the
 * ladder subscribes internally for the *version* and paints through refs, so a
 * book delta at 20 Hz produces no React work at all (plan §2.3).
 *
 * **The comment above used to say "memoised on `marketId` alone" and the props
 * said otherwise** — `opensInSeconds` was computed in `<TradeRoute>` from a
 * route-level `useServerNow()`, so through the whole of `FIXING` this memo
 * could never bail out and neither could the page above it (`docs/17`). It
 * takes the instant now and counts down against its own clock, which is the
 * only prop shape that lets both memos hold.
 */
/**
 * **Loaded on intent, never on cold load.** Pro is off by default, so an
 * anonymous visitor must not carry the ladder, its gate or its click path — the
 * same rule `wallet/lazy.ts` applies to the signing stack, for the same reason
 * and enforced by the same budget (invariant 19). Eagerly imported it put the
 * cold load 1% over.
 */
const ProLadderPanel = lazy(() =>
  import('@/components/market/ProLadderPanel').then((m) => ({ default: m.ProLadderPanel })),
)

/**
 * The pro ladder, in `<BookPanel>`'s seat.
 *
 * Split in two for `<BookPanel>`'s own reason: the `opens in {n}s` countdown
 * needs a 4 Hz clock, and an `OPEN` market — which is nearly the whole of one —
 * must not pay for it. The panel below holds its own `useServerNow()` for the
 * market-state gate, so the split buys only the countdown here; it is kept
 * anyway so the two ladders have one shape and neither has to be reasoned about
 * separately.
 */
export const ProBookPanel = memo(function ProBookPanel({
  opensAtMs,
  ...rest
}: Omit<ProLadderPanelProps, 'opensInSeconds'> & { opensAtMs: number | null }) {
  return (
    // The panel's own frame while the chunk arrives, so the column has its
    // shape before its content — a blank region here would be the layout
    // jumping on the one gesture that asked for it.
    <Suspense fallback={
        <Panel header="Pro ladder" className="min-h-0 flex-1">
          <div />
        </Panel>
      }>
      {opensAtMs === null ?
        <ProLadderPanel {...rest} opensInSeconds={null} />
      : <PreOpenProPanel {...rest} opensAtMs={opensAtMs} />}
    </Suspense>
  )
})

const PreOpenProPanel = memo(function PreOpenProPanel({
  opensAtMs,
  ...rest
}: Omit<ProLadderPanelProps, 'opensInSeconds'> & { opensAtMs: number }) {
  const nowMs = useServerNow()
  return <ProLadderPanel {...rest} opensInSeconds={(opensAtMs - nowMs) / 1000} />
})

export const BookPanel = memo(function BookPanel({ opensAtMs, ...rest }: BookPanelProps) {
  // **Two components rather than one hook behind a condition**, and the reason
  // is not the rules of hooks. A `useServerNow()` on this line would tick the
  // ladder wrapper four times a second for the whole life of an `OPEN` market —
  // which is nearly all of it — and that is this row's own defect moved one
  // component down rather than removed. The countdown exists only before the
  // open, so the subscription does too.
  if (opensAtMs === null) return <BookBody {...rest} opensInSeconds={null} />
  return <PreOpenBookPanel {...rest} opensAtMs={opensAtMs} />
})

const PreOpenBookPanel = memo(function PreOpenBookPanel({
  opensAtMs,
  ...rest
}: Omit<BookPanelProps, 'opensAtMs'> & { opensAtMs: number }) {
  const nowMs = useServerNow()
  return <BookBody {...rest} opensInSeconds={(opensAtMs - nowMs) / 1000} />
})

/**
 * **Loaded on intent, never on cold load** — `<ProLadderPanel>`'s rule. Click
 * to trade is off by default, so its gate and click path are not on an
 * anonymous visitor's cold load (invariant 19).
 */
const BookTradingLadder = lazy(() =>
  import('@/components/market/BookTradingLadder').then((m) => ({ default: m.BookTradingLadder })),
)

/**
 * Which ladder the book is: the clickable one while click to trade is on and
 * the ticket is beside it, the read-only one otherwise.
 *
 * Both subscriptions here are to one field each (`useStoreField`): the switch
 * changes on a click and the outcome on a click, while the size beside the
 * outcome in the link store changes on every keystroke and is read only by the
 * clickable ladder itself.
 *
 * The read-only ladder still shows the account's own orders and cancels one on
 * its ✕ — cancelling is never gated, and needs neither the switch nor the
 * ticket.
 */
const BookBody = memo(function BookBody({
  marketId,
  opensInSeconds,
  clickable = false,
  symbol,
  marketState,
  openAtMs,
  expiryAtMs,
  upTokenId = null,
  downTokenId = null,
  limits,
  socketStale = false,
  className,
}: Omit<BookPanelProps, 'opensAtMs'> & { opensInSeconds: number | null }) {
  const runtime = useRuntime()
  const clickToTrade = useStoreField(uiStore, (s) => s.clickToTrade)
  const linked = useStoreField(ticketLinkStore, (s) => s.outcome)
  const outcome: Outcome = clickable ? linked : 'UP'

  const onCancel = useCallback(
    (side: LadderSide, cents: number, row: HTMLElement) => {
      if (marketId === null) return
      void cancelOwnOnRow(runtime, marketId, outcome, side, cents, row)
    },
    [runtime, marketId, outcome],
  )

  const plain = (
    <OrderbookLadder
      marketId={marketId}
      outcome={outcome}
      opensInSeconds={opensInSeconds}
      onCancel={onCancel}
      {...(className ? { className } : {})}
    />
  )

  const trades =
    clickable &&
    clickToTrade &&
    symbol !== undefined &&
    marketState !== undefined &&
    openAtMs !== undefined &&
    expiryAtMs !== undefined &&
    limits !== undefined
  if (!trades) return plain

  return (
    // The read-only ladder while the chunk arrives: the same rows in the same
    // place, so ticking the box moves nothing.
    <Suspense fallback={plain}>
      <BookTradingLadder
        marketId={marketId}
        symbol={symbol}
        marketState={marketState}
        openAtMs={openAtMs}
        expiryAtMs={expiryAtMs}
        upTokenId={upTokenId}
        downTokenId={downTokenId}
        limits={limits}
        socketStale={socketStale}
        outcome={outcome}
        opensInSeconds={opensInSeconds}
        {...(className ? { className } : {})}
      />
    </Suspense>
  )
})

// ---------------------------------------------------------------------------

export interface ResolutionSurfaceProps {
  /** The one-line bar below 768px: no receipt card, and the dismiss inside the
   *  line (`<ResolutionPanel compact>`). */
  compact?: boolean
  /** The market that expired at the current anchor. `null` outside the hold
   *  window, which is most of the minute. */
  marketId: string | null
  symbol: string
  stale: boolean
  /** Below `lg` the surface is a card above the tables and the user may close
   *  it; in the ticket column it is simply there and closing it would leave a
   *  hole (`components.md` §2.8). */
  onDismiss?: () => void
  className?: string
}

/**
 * The secondary surface: market N resolving behind market N+1 (§6.3).
 *
 * **The primary column belongs to the tradable market at all times.** N's
 * resolution is secondary from the instant it stops being tradable and never
 * becomes primary again — not while resolving, not at `RESOLVED`, not for the
 * receipt. Everything about this component's placement follows from that one
 * line: it sits *under* the ticket where there is a column and *above* the
 * tables where there is not, it never grows, and it never animates across
 * anything.
 *
 * It renders nothing for nearly the whole minute, which is the other half of
 * the design: for about fifty-seven and a half seconds of every sixty there is
 * one market on screen. It was forty-seven until §F26 cut
 * `resolve_eta_offset_ms` from 13 000 to 2 000 — the number is derived from
 * `/config` and is written here only to describe the shape, never read.
 */
export const ResolutionSurface = memo(function ResolutionSurface({
  marketId,
  symbol,
  stale,
  onDismiss,
  compact = false,
  className,
}: ResolutionSurfaceProps) {
  // 4 Hz, and it is this component's own subscription rather than the route's —
  // the determinate bar and the settlement window both move on it, and the
  // route has no use for a clock that ticks four times a second.
  const nowMs = useServerNow()
  if (!marketId) return null
  if (compact) {
    return (
      <div data-testid="resolution-surface" className={cx('relative', className)}>
        {/* Mounted for its toast, which a phone still gets; the receipt card
            itself would jump the book down for a second every minute. */}
        <SettlementReceiptStack showReceipt={false} />
        <ResolutionPanel
          marketId={marketId}
          symbol={symbol}
          nowMs={nowMs}
          stale={stale}
          compact
          {...(onDismiss ? { onDismiss } : {})}
        />
      </div>
    )
  }
  return (
    <div data-testid="resolution-surface" className={cx('group relative', className)}>
      {/*
        **Shown only beside something it can dismiss.** The panel and the
        receipt each render nothing once there is nothing to say — a market this
        account held no position in, settled — and the button used to stay
        behind on its own: a lone `×` over an empty strip above the tables. CSS
        rather than state, because whether either child drew anything is theirs
        to decide and they already decide it by rendering or not.
      */}
      {onDismiss ? (
        <button
          type="button"
          aria-label="dismiss resolution"
          onClick={onDismiss}
          className="absolute top-1 right-1 z-10 hidden group-has-[[data-testid=resolution-panel]]:inline-flex group-has-[[data-testid=settlement-receipt]]:inline-flex min-h-[var(--ud-tap-min)] min-w-[var(--ud-tap-min)] items-center justify-center rounded-sm px-1 text-body-sm text-text-tertiary hover:text-text-primary focus-visible:shadow-focus focus-visible:outline-none md:min-h-0 md:min-w-0"
        >
          ×
        </button>
      ) : null}
      {/* The receipt first: it is what the panel *becomes*, and while both are
          mounted the receipt is the newer statement of the same market. */}
      <SettlementReceiptStack />
      <ResolutionPanel marketId={marketId} symbol={symbol} nowMs={nowMs} stale={stale} />
    </div>
  )
})

// ---------------------------------------------------------------------------

/** Label over value in a phone's grid cell; inline from 768px (`<MarketStrip>`). */
function Stat({ label, value }: { label: string; value: string }) {
  return (
    <span className="flex min-w-0 flex-col md:shrink-0 md:flex-row md:items-baseline md:gap-1">
      <span className="text-micro text-text-tertiary">{label}</span>
      <NumCell value={value} flash={false} align="left" className="text-body-sm text-text-primary" />
    </span>
  )
}

/**
 * `up_price` / `down_price` are **integer cents** under names that do not say
 * so — `asyncapi.TickerData` spells them that way and `openapi` has no ticker
 * object to overrule it, so the client binds to the name that exists rather than
 * to the one that reads better (register §F18f, and §F6 on why that boundary is
 * not ours to move).
 */
function centsOf(ticker: unknown, field: 'up_price' | 'down_price'): string {
  const value = (ticker as Record<string, unknown> | null)?.[field]
  return typeof value === 'number' ? formatCents(value) : '—'
}

function usdcOf(ticker: unknown, field: string): string {
  const value = (ticker as Record<string, unknown> | null)?.[field]
  return typeof value === 'string' ? formatUsdcCompact(value) : '—'
}

